import React, { useContext } from 'react'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'
import { PageHeaderPluginActionsContext } from '@/components/ui/page-header-plugin-actions'
import { PageHeading, hasVisiblePageHeaderDescription, type PageHeadingSize } from '@/components/ui/page-heading'

/** When `layout.showPageTitleIcons` is false, header title icons are hidden for the whole tenant. Omitted defaults to true. */
function shouldShowPageTitleIcons(): boolean {
  return (CURRENT_CLIENT as { layout?: { showPageTitleIcons?: boolean } }).layout?.showPageTitleIcons !== false
}

const toolbarToTitleMb = 'mb-6'

interface PageHeaderProps {
  /** Omit when another visible primary heading exists on the page (e.g. profile name). */
  title?: React.ReactNode
  /** May appear without `title` for subtitle-only chrome; ensure accessibility (another `h1` or equivalent elsewhere when needed). */
  description?: React.ReactNode
  icon?: React.ReactNode
  /** Merged with the default icon frame (e.g. `!rounded-full !bg-transparent p-0` for an avatar) */
  iconClassName?: string
  leading?: React.ReactNode
  actions?: React.ReactNode
  /** `settings` — narrower title scale for settings-class surfaces (`text-xl`). */
  size?: PageHeadingSize
  className?: string
}

export function PageHeader({
  title,
  description,
  icon,
  iconClassName,
  leading,
  actions,
  size = 'default',
  className,
}: PageHeaderProps) {
  const plugin = useContext(PageHeaderPluginActionsContext)
  const pluginActions = title ? plugin?.actions : null
  const below = title ? plugin?.below : null
  if (below) {
    return (
      <div className="flex w-full flex-col gap-4">
        <PageHeaderPluginActionsContext.Provider value={null}>
          <PageHeader
            title={title}
            description={description}
            icon={icon}
            iconClassName={iconClassName}
            leading={leading}
            actions={
              pluginActions ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {pluginActions}
                  {actions}
                </div>
              ) : (
                actions
              )
            }
            size={size}
            className={className}
          />
        </PageHeaderPluginActionsContext.Provider>
        {below}
      </div>
    )
  }
  const headerActions =
    pluginActions ? (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {pluginActions}
        {actions}
      </div>
    ) : (
      actions
    )
  const showTitleIcon = shouldShowPageTitleIcons() && Boolean(icon)
  const hasTitle = Boolean(title)
  const hasDescription = hasVisiblePageHeaderDescription(description)

  const titleBlock =
    hasTitle || hasDescription || (showTitleIcon && icon) ? (
      <PageHeading
        title={title}
        description={description}
        icon={icon}
        iconClassName={iconClassName}
        showTitleIcon={showTitleIcon}
        level="h1"
        size={size}
      />
    ) : null

  if (headerActions) {
    if (!titleBlock && !leading) {
      return (
        <div className={cn('flex justify-end', className)}>
          {headerActions}
        </div>
      )
    }

    if (leading) {
      return (
        <div className={cn('flex w-full flex-col gap-0', className)}>
          <div
            className={cn(
              'flex w-full items-center justify-between gap-4',
              titleBlock && toolbarToTitleMb,
            )}
          >
            <div className="min-w-0">{leading}</div>
            <div className="shrink-0">{headerActions}</div>
          </div>
          {titleBlock ? <div className="w-full min-w-0">{titleBlock}</div> : null}
        </div>
      )
    }

    if (!titleBlock) {
      return (
        <div className={cn('flex justify-end', className)}>
          {headerActions}
        </div>
      )
    }

    return (
      <div className={cn('flex w-full items-center justify-between gap-4', className)}>
        <div className="min-w-0 flex-1">{titleBlock}</div>
        <div className="shrink-0">{headerActions}</div>
      </div>
    )
  }

  if (leading) {
    return (
      <div className={cn('flex w-full flex-col gap-0', className)}>
        <div className={cn(titleBlock && toolbarToTitleMb)}>{leading}</div>
        {titleBlock}
      </div>
    )
  }

  if (!titleBlock) {
    return null
  }

  return className ? <div className={className}>{titleBlock}</div> : titleBlock
}
